import { useMemo } from 'react'
import { omit } from 'lodash'
import { StyleSheet, View, ViewProps } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { AccessType, CompanyData, DataWithUpload, DEFAULT_OCCUPANCY_COLORS, Draft, useCompany } from 'zens-store'
import { Button, RadioGroup, Select, Spacing, Text } from 'zens-storybook'
import { Link } from 'zens-router'
import { usePermissions } from '@dms/hook'

import { NavigationConfirm } from '../navigation'

import OccupancyColor, { OCCUPANCY_COLOR_LABELS } from './occupancycolor'

const styles = StyleSheet.create({
  field: { marginTop: Spacing.M, maxWidth: 400 },
  label: { marginBottom: Spacing.XS },
  buttons: { marginTop: Spacing.L, flexDirection: 'row', gap: Spacing.S },
})

interface Props extends ViewProps {
  companyId: number
  onSave?: (draft: Draft<DataWithUpload<CompanyData>>) => Promise<void>
  loading?: boolean
}

const CompanyOccupancyColorsForm = ({ companyId, onSave, loading, ...props }: Props) => {
  const { t } = useTranslation()
  const company = useCompany({ companyId })
  const { hasPermission } = usePermissions(companyId)
  const canEdit = hasPermission(AccessType.Update, 'settings')
  const draft = useMemo(
    () =>
      new Draft<DataWithUpload<CompanyData>>({
        ...omit(company.data?.data, 'colorScheme'),
        id: companyId,
        color_scheme: {
          ...company.data?.data.color_scheme,
          occupancy_colors: { ...company.data?.configuredOccupancyColors },
        },
      }),
    [companyId, company.data],
  )
  const enabled = Object.keys(draft.data.color_scheme?.occupancy_colors || {}).length > 0
  const setEnabled = (value: string) => {
    const nextEnabled = value === 'enabled'
    if (!canEdit || loading || nextEnabled === enabled) return
    draft.update('color_scheme', {
      ...draft.data.color_scheme,
      occupancy_colors: nextEnabled ? { ...DEFAULT_OCCUPANCY_COLORS } : {},
    })
  }
  const items = Object.entries(OCCUPANCY_COLOR_LABELS).map(([value, label]) => ({
    value,
    title: t(label),
    render: () => <OccupancyColor value={value} />,
  }))

  const update = (label: string, value: string) => {
    draft.update('color_scheme', {
      ...draft.data.color_scheme,
      occupancy_colors: { ...draft.data.color_scheme?.occupancy_colors, [label]: value },
    })
  }

  return (
    <View {...props}>
      <Text level={3}>{t('Occupancy colors')}</Text>
      <RadioGroup
        style={styles.field}
        accessibilityLabel={t('Occupancy colors on device')}
        items={[
          { value: 'enabled', title: t('Enabled') },
          { value: 'disabled', title: t('Disabled') },
        ]}
        value={enabled ? 'enabled' : 'disabled'}
        onSelect={setEnabled}
        disabled={loading || !canEdit}
        testID="CompanyOccupancyColorsForm.Enabled"
      />
      {Object.entries(draft.data.color_scheme?.occupancy_colors || {}).map(([label, value], index, colors) => (
        <View key={label} style={[styles.field, { zIndex: (colors.length - index) * 100 }]}>
          <Text style={styles.label}>
            {label === 'free' ? t('Free') : label === 'occupied' ? t('Occupied') : label.replace(/_/g, ' ')}
          </Text>
          <Select
            accessibilityLabel={label === 'free' ? t('Free') : label === 'occupied' ? t('Occupied') : label}
            items={items}
            value={value}
            renderValue={() => <OccupancyColor value={value} />}
            onSelect={(color: string) => update(label, color)}
            disabled={loading || !canEdit}
            testID={`CompanyOccupancyColorsForm.${label}`}
          />
        </View>
      ))}
      <View style={styles.buttons}>
        <Button
          title={t('Save')}
          size="L"
          onPress={() => canEdit && onSave?.(draft)}
          disabled={loading || !canEdit}
          testID="CompanyOccupancyColorsForm.Save"
        />
        <Link href={`/companies/${companyId}/detail`} asChild>
          <Button title={t('Cancel')} light size="L" disabled={loading} testID="CompanyOccupancyColorsForm.Cancel" />
        </Link>
      </View>
      <NavigationConfirm draft={draft} />
    </View>
  )
}

CompanyOccupancyColorsForm.Skeleton = () => (
  <View>
    <Text.Skeleton level={3} />
    <RadioGroup.Skeleton numberOfOptions={2} style={styles.field} />
    <Select.Skeleton style={styles.field} />
    <Select.Skeleton style={styles.field} />
    <View style={styles.buttons}>
      <Button.Skeleton size="L" />
    </View>
  </View>
)

export default observer(CompanyOccupancyColorsForm)
